import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Tag, Search } from 'lucide-react';

const Store = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchProducts();
    loadCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const fetchProducts = async () => {
    try {
      const url = selectedCategory
        ? `${API_URL}/api/store/products?category=${selectedCategory}`
        : `${API_URL}/api/store/products`;
      const response = await fetch(url);
      const data = await response.json();
      setProducts(data.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCart = () => {
    const sessionId = getSessionId();
    // Cart is managed on backend, load it via API
  };

  const getSessionId = () => {
    let sessionId = localStorage.getItem('cart_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('cart_session_id', sessionId);
    }
    return sessionId;
  };

  const addToCart = async (product) => {
    try {
      const sessionId = getSessionId();
      const response = await fetch(`${API_URL}/api/store/cart/${sessionId}/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          price_usd: product.price_usd,
          price_crypto: product.price_crypto,
          image_url: product.image_url
        })
      });
      const data = await response.json();
      setCart(data.items || []);
      alert(`${product.name} added to cart!`);
    } catch (error) {
      console.error('Failed to add to cart:', error);
      alert('Failed to add to cart');
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categories = [
    { value: 'digital', label: 'Digital Products' },
    { value: 'ai_personality', label: 'AI Personalities' },
    { value: 'course', label: 'Courses' },
    { value: 'document', label: 'Documents' },
    { value: 'service', label: 'Services' }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-cyan-500/30 bg-black/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-cyan-400 font-['Orbitron']">Federation Store</h1>
              <p className="text-gray-400 mt-1">NextXus Consciousness Products</p>
            </div>
            <Link
              to="/store/cart"
              className="bg-cyan-500/20 border border-cyan-500 px-6 py-3 rounded hover:bg-cyan-500/30 transition-all flex items-center gap-2"
            >
              <ShoppingCart size={20} />
              Cart ({cart.length})
            </Link>
          </div>

          {/* Search & Filter */}
          <div className="mt-6 flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 text-gray-500" size={20} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-black/50 border border-cyan-500/30 rounded px-10 py-2 focus:outline-none focus:border-cyan-500"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="mt-4 flex gap-2 flex-wrap">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded border ${
                selectedCategory === null
                  ? 'bg-cyan-500 border-cyan-500 text-black'
                  : 'bg-black/50 border-cyan-500/30 hover:border-cyan-500'
              } transition-all`}
            >
              All Products
            </button>
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-4 py-2 rounded border ${
                  selectedCategory === cat.value
                    ? 'bg-cyan-500 border-cyan-500 text-black'
                    : 'bg-black/50 border-cyan-500/30 hover:border-cyan-500'
                } transition-all`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-gray-400">Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-xl text-gray-400">No products found</p>
            <p className="text-gray-500 mt-2">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 rounded-lg overflow-hidden hover:border-cyan-500 transition-all group"
              >
                {/* Product Image */}
                <div className="h-48 bg-gradient-to-br from-cyan-500/10 to-purple-500/10 flex items-center justify-center">
                  {product.image_url ? (
                    <img
          decoding="async"
          loading="lazy"
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Tag className="w-16 h-16 text-cyan-500/50" />
                  )}
                </div>

                {/* Product Info */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-xl font-bold text-cyan-400 group-hover:text-cyan-300 transition-colors">
                      {product.name}
                    </h3>
                    <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded">
                      {product.category.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>

                  {/* Features */}
                  {product.features && product.features.length > 0 && (
                    <div className="mb-4">
                      <ul className="text-xs text-gray-500 space-y-1">
                        {product.features.slice(0, 3).map((feature) => (
                          <li key={feature}>• {feature}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Price & Action */}
                  <div className="flex items-center justify-between pt-4 border-t border-cyan-500/20">
                    <div>
                      <div className="text-2xl font-bold text-white">
                        ${product.price_usd}
                      </div>
                      {product.price_crypto && (
                        <div className="text-xs text-gray-500">
                          {product.price_crypto} {product.currency || 'USDC'}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => addToCart(product)}
                      className="bg-cyan-500 hover:bg-cyan-400 text-black px-6 py-2 rounded font-bold transition-all"
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Store;
